import {
  CustomTooltip,
  StyledReactSelect,
  theme,
  Trashcan,
} from '@fountain/fountain-ui-components';
import { Grid, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Error } from 'components/Error';
import { FilterLocationGroupDropdown } from 'components/FilterDropdown/FilterLocationGroupDropdown/FilterLocationGroupDropdown';
import { FilterOpeningDropdown } from 'components/FilterDropdown/FilterOpeningDropdown';
import { FilterStageDropdown } from 'components/FilterDropdown/FilterStageDropdown';
import { Filterable } from 'components/FilterDropdown/util';
import InfoIcon from 'images/InfoIcon';

import { conditionTypes } from '../constants';
import { messages } from '../messages';
import { updateConditionValue } from '../StageSelector/utils';
import { useErrors } from './hooks/useErrors';
import { useStyles } from './styles';
import { ConditionBuilderProps, PreviousChange } from './types';

export const ConditionBuilder: VFC<ConditionBuilderProps> = ({
  condition,
  index,
  dynamicConditions,
  setDynamicConditions,
  handleChange,
  errors,
  hostId,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [prevCondition, setPrevCondition] = useState<PreviousChange>({
    title: 'stage_titles',
    index: 0,
  });
  const {
    stageError,
    openingError,
    locationGroupError,
    setStageError,
    setOpeningError,
    setLocationGroupError,
  } = useErrors(errors);

  const handleDeleteCondition = (index: number) => {
    if (dynamicConditions.length > 1) {
      const updatedConditions = [...dynamicConditions];
      updatedConditions.splice(index, 1);
      setDynamicConditions(updatedConditions);
      updateConditionValue(
        dynamicConditions[index].type,
        handleChange,
        undefined,
      );
    }
  };

  const handleConditionValueChange = (index: number, values: Filterable[]) => {
    const updatedConditions = [...dynamicConditions];
    updatedConditions[index].values = values;
    setDynamicConditions(updatedConditions);

    if (
      updatedConditions[index].type !== prevCondition.title &&
      index === prevCondition.index
    ) {
      handleChange({
        [prevCondition.title]: undefined,
      });
    }
    // Update state with merged values
    switch (updatedConditions[index].type) {
      case 'stage':
        handleChange({
          stage_titles: updatedConditions[index].values.map(obj => obj.title),
        });
        setPrevCondition({ title: 'stage_titles', index });
        setStageError(false);
        break;
      case 'opening':
        handleChange({
          opening_titles: updatedConditions[index].values.map(obj => obj.title),
        });
        setPrevCondition({ title: 'opening_titles', index });
        setOpeningError(false);
        break;
      case 'location_group':
        handleChange({
          location_group_titles: updatedConditions[index].values.map(
            obj => obj.title,
          ),
        });
        setPrevCondition({ title: 'location_group_titles', index });
        setLocationGroupError(false);
        break;
      default:
        break;
    }
  };

  const handleConditionTypeChange = (index: number, value: string) => {
    const updatedConditions = [...dynamicConditions];
    const currentCondition = updatedConditions[index];

    updateConditionValue(currentCondition.type, handleChange, undefined);

    currentCondition.type = value;
    currentCondition.values = [];

    handleConditionValueChange(index, []);

    setDynamicConditions(updatedConditions);
  };

  const availableConditionTypes = () => {
    const existingTypes = dynamicConditions.map(condition => condition.type);
    return conditionTypes.filter(conditionType => {
      return !existingTypes.includes(conditionType.value);
    });
  };

  return (
    <>
      {index > 0 && <div className={styles.separator}> AND </div>}
      <Grid className={styles.conditionBuilderContainer}>
        {/* Dropdown for selecting condition type */}
        <Grid>
          <StyledReactSelect
            label={
              <Typography variant="body2" color="textPrimary">
                <FormattedMessage {...messages.conditionType} />
                <CustomTooltip
                  title={
                    <FormattedMessage {...messages.conditionTypeTooltip} />
                  }
                  dense
                  placement="top"
                  className={styles.tooltipIcon}
                >
                  <InfoIcon color={theme.palette.common.gray800} />
                </CustomTooltip>
              </Typography>
            }
            aria-label={intl.formatMessage(messages.openingStage)}
            options={availableConditionTypes()}
            value={conditionTypes.find(type => type.value === condition.type)}
            onChange={(option: { value: string }) =>
              handleConditionTypeChange(index, option.value)
            }
            getOptionLabel={(option: { label: string }) => option.label}
            getOptionValue={(option: { value: string }) => option.value}
            className={styles.conditionType}
          />
        </Grid>
        {/* Is any of text */}
        <Grid className={styles.isAnyOfText}>
          <Typography variant="body2" color="textPrimary">
            <FormattedMessage {...messages.isAnyOf} />
          </Typography>
        </Grid>
        {/* Dropdown for selecting condition value */}
        <Grid className={styles.conditionValue}>
          <Typography variant="body2" className={styles.nestedRequired}>
            <FormattedMessage {...messages.value} />
          </Typography>
          {condition.type === 'stage' && (
            <>
              <FilterStageDropdown
                selected={condition.values}
                setSelected={values =>
                  handleConditionValueChange(index, values)
                }
                showTitle
                error={stageError}
                userId={hostId}
              />
              {stageError && <Error error={stageError} />}
            </>
          )}
          {condition.type === 'opening' && (
            <>
              <FilterOpeningDropdown
                selected={condition.values}
                setSelected={values =>
                  handleConditionValueChange(index, values)
                }
                showTitle
                enterprise
                userId={hostId}
                error={openingError}
              />
              {openingError && <Error error={openingError} />}
            </>
          )}
          {condition.type === 'location_group' && (
            <>
              <FilterLocationGroupDropdown
                selected={condition.values}
                setSelected={values =>
                  handleConditionValueChange(index, values)
                }
                showTitle
                enterprise
                userId={hostId}
                error={locationGroupError}
              />
              {locationGroupError && <Error error={locationGroupError} />}
            </>
          )}
        </Grid>

        {/* Delete button for removing condition */}
        <div className={styles.trashIconContainer}>
          <Trashcan
            viewBox="0 0 16 16"
            onClick={() => handleDeleteCondition(index)}
            className={classNames(styles.trashIcon, {
              [styles.trashIconDisabled]: dynamicConditions.length === 1,
            })}
            data-testid="trash-icon"
          />
        </div>
      </Grid>
    </>
  );
};
